'use client';

import cn from 'classnames';
import Image from '@components/ui/image';
import usePrice from '@framework/product/use-price';
import { Product, ProductVariant, Store } from '@framework/types';
import { useModalAction } from '@components/common/modal/modal.context';
import useWindowSize from '@utils/use-window-size';
import { Eye } from '@components/icons/eye-icon';
import { useCart } from '@contexts/cart/cart.context';
import { useRouter } from 'next/navigation';
import { productPlaceholder } from '@assets/placeholders';
import dynamic from 'next/dynamic';
import { useTranslation } from 'src/app/i18n/client';
import { useMemo } from 'react';
import { useIntersection } from '../../../hooks/use-intersection';

const AddToCart = dynamic(() => import('@components/product/add-to-cart'), {
  ssr: false,
});

interface ProductProps {
  lang: string;
  product: Product & {
    variants?: ProductVariant[];
    store?: Store;
    unit?: string;
    product_type?: string;
    discount?: number;
    unit_type?: string;
    unit_quantity?: string;
    packaging?: string;
    date_to?: string;
    valid_from?: string;
  };
  className?: string;
  href: string;
}

function RenderPopupOrAddToCart({ props }: { props: Object }) {
  let { data, lang }: any = props;
  const { t } = useTranslation(lang, 'common');
  const { id, quantity, product_type } = data ?? {};
  const { width } = useWindowSize();
  const { openModal } = useModalAction();
  const { isInCart, isInStock } = useCart();
  const iconSize = width! > 1024 ? '19' : '17';
  const outOfStock = isInCart(id) && !isInStock(id);
  
  function handlePopupView(e: React.MouseEvent) {
    e.stopPropagation();
    openModal('PRODUCT_VIEW', data);
  }

  if (product_type === 'variable') {
    return (
      <button
        className="inline-flex items-center justify-center w-8 h-8 text-4xl rounded-full bg-brand lg:w-10 lg:h-10 text-brand-light focus:outline-none focus-visible:outline-none"
        aria-label="Count Button"
        onClick={handlePopupView}
      >
        <Eye width={iconSize} height={iconSize} opacity="1" />
      </button>
    );
  }
  return <AddToCart data={data} variant="mercury" lang={lang} />;
}

function ProductCardAlpine({
  product,
  className,
  lang,
  href,
}: ProductProps) {
  const { t } = useTranslation(lang, 'common');
  const { name, image_url, unit, product_type, price, discountedPrice, variants, store, date_to, unit_quantity, valid_from } = product ?? {};
  const { width } = useWindowSize();
  const router = useRouter();
  const { openModal } = useModalAction();

  const [cardRef, isVisible] = useIntersection({
    threshold: 0,
    rootMargin: '50px',
    freezeOnceVisible: true
  });

  const discountPercentage = useMemo(() => {
    if (!price || !discountedPrice) {
      return null;
    }
    
    const priceStr = price.toString();
    const discountedPriceStr = discountedPrice.toString();
    
    const originalPrice = parseFloat(priceStr.replace(/[^0-9.]/g, ''));
    const finalPrice = parseFloat(discountedPriceStr.replace(/[^0-9.]/g, ''));
    
    if (isNaN(originalPrice) || isNaN(finalPrice) || originalPrice <= 0) {
      return null;
    }
    
    if (finalPrice >= originalPrice) {
      return null;
    }
    
    return Math.round(((originalPrice - finalPrice) / originalPrice) * 100);
  }, [price, discountedPrice]);

  const pricePerUnit = useMemo(() => {
    if (!unit_quantity) return null;
    
    const currentPrice = discountedPrice || price;
    if (!currentPrice) return null;

    const priceNum = typeof currentPrice === 'string' 
      ? parseFloat(currentPrice.replace(/[^0-9.]/g, ''))
      : currentPrice;

    const quantityNum = parseFloat(unit_quantity);

    if (isNaN(priceNum) || isNaN(quantityNum) || quantityNum === 0) return null;

    return (priceNum / quantityNum).toFixed(2);
  }, [price, discountedPrice, unit_quantity]);

  const handleClick = () => {
    router.push(href);
  };

  const handlePopupView = () => {
    openModal('PRODUCT_VIEW', product);
  };

  const productStore = store || (variants?.[0]?.store);

  // Use consistent date formatting options for server and client
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Use explicit format to avoid locale differences
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '';
    }
  };

  const formatShortDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}`;
    } catch (error) {
      return '';
    }
  };

  const placeholderImage = product?.image?.original ?? productPlaceholder;

  return (
    <article
      ref={cardRef as any}
      className={cn(
        'flex flex-col group overflow-hidden rounded-md cursor-pointer transition-all duration-300 shadow-card hover:shadow-cardHover relative h-full',
        className,
      )}
      title={name}
    >
      <div className="relative shrink-0" onClick={handleClick}>
        <div className="relative flex justify-center overflow-hidden w-auto h-48 transition duration-500 scale-90 group-hover:scale-100">
          {isVisible && (
            <Image
              src={image_url || placeholderImage}
              alt={name || 'Product Image'}
              width={240}
              height={240}
              quality={100}
              className="object-contain transition duration-200 ease-in rounded-md group-hover:scale-105"
              loading="lazy"
            />
          )}
        </div>
        <div className="w-full h-full absolute top-0 pt-2.5 md:pt-3.5 px-3 md:px-4 lg:px-[18px] z-10 -mx-0.5 sm:-mx-1">
          {discountPercentage && (
            <span className="text-[11px] md:text-xs font-bold text-brand-light uppercase inline-block bg-brand rounded-full px-2.5 pt-1 pb-[3px] mx-0.5 sm:mx-1">
              {t('text-on-sale')}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col px-3 md:px-4 lg:px-[18px] pb-5 lg:pb-6 lg:pt-1.5 h-full" onClick={handleClick}>
        {(valid_from || date_to) && (
          <div className="mb-1.5 text-xs text-gray-500">
            {date_to && (
              <div className="flex items-center gap-2">
                <span>
                  {t('text-valid-to')}: {formatDate(date_to)}
                </span>
                {discountPercentage > 0 && (
                  <span className="inline-block text-11px font-bold text-white bg-red-500 rounded-full px-2 py-0.5">
                    -{discountPercentage}%
                  </span>
                )}
              </div>
            )}
            {valid_from && (
              <div>
                {t('text-valid-from')}: {formatDate(valid_from)}
              </div>
            )}
          </div>
        )}
        <h3 className="text-brand-dark text-13px sm:text-sm lg:text-15px leading-5 sm:leading-6 mb-1.5">
          {name}
        </h3>
        <div className="mb-1 lg:mb-1.5 -mx-1 flex items-center flex-wrap">
          {discountedPrice ? (
            <>
              <span className="inline-block mx-1 text-15px font-bold sm:text-16px lg:text-lg text-green-600">
                €{discountedPrice}
              </span>
              {discountPercentage > 0 && (
                <del className="mx-1 text-13px text-brand-dark text-opacity-70 text-red-600 font-bold lg:text-lg">
                  €{price}
                </del>
              )}
              {discountPercentage > 0 && (
                <div className="flex flex-wrap items-center">
                  <span className="inline-block mx-1 text-11px font-bold text-red-600 bg-red-100 rounded px-1.5 py-0.5">
                    -{discountPercentage}%
                    {date_to ? ` (${formatShortDate(date_to)})` : ''}
                  </span>
                </div>
              )}
            </>
          ) : (
            <span className="inline-block mx-1 text-15px font-bold sm:text-16px lg:text-lg text-brand-dark">
              €{price}
            </span>
          )}
          {pricePerUnit && (
            <span className="inline-block mx-1 text-11px text-gray-500">
              (€{pricePerUnit}/ {t(product.unit_type?.toLowerCase())} {t('unit')})
            </span>
          )}
        </div>
        
        <div className="flex flex-col gap-0.5 text-13px sm:text-sm text-gray-600">
          {product.unit_type && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{t('Unit')}:</span>
              <span>{product.unit_type}</span>
            </div>
          )}
          {product.unit_quantity && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{t('Quantity')}:</span>
              <span>{product.unit_quantity}</span>
            </div>
          )}
          {product.packaging && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{t('Packaging')}:</span>
              <span>{product.packaging}</span>
            </div>
          )}
        </div>

        <div className="mt-auto text-13px sm:text-sm">{unit}</div>
        {productStore?.image_url && (
          <div className="mb-1 h-8 relative mt-4">
            <Image
              src={productStore.image_url}
              alt={productStore.name}
              fill
              sizes="40px"
              className="object-contain ml-auto"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </article>
  );
}

export default ProductCardAlpine;
